import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard, Users, ClipboardList, CalendarDays, MapPin, Route as RouteIcon,
  FileText, UserCog, Settings as SettingsIcon, Mail, ShieldCheck, LogOut, Menu, X, Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/route", label: "Today's Route", icon: RouteIcon, testid: "nav-route" },
  { to: "/app/jobs", label: "Jobs", icon: ClipboardList, testid: "nav-jobs" },
  { to: "/app/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/app/map", label: "Map", icon: MapPin, testid: "nav-map" },
  { to: "/app/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/app/invoices", label: "Invoices", icon: FileText, testid: "nav-invoices" },
  { to: "/app/staff", label: "Staff", icon: UserCog, testid: "nav-staff" },
  { to: "/app/emails", label: "Email Log", icon: Mail, testid: "nav-emails" },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

const MOBILE_BOTTOM = [
  { to: "/app/route", label: "Route", icon: RouteIcon, testid: "mobile-nav-route" },
  { to: "/app/jobs", label: "Jobs", icon: ClipboardList, testid: "mobile-nav-jobs" },
  { to: "/app/map", label: "Map", icon: MapPin, testid: "mobile-nav-map" },
  { to: "/app/dashboard", label: "Home", icon: LayoutDashboard, testid: "mobile-nav-home" },
  { to: "/app/customers", label: "Customers", icon: Users, testid: "mobile-nav-customers" },
];

export default function AppShell() {
  const { user, business, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  const bname = business?.branding?.business_name || business?.name || "WindowIQX";
  const sub = business?.subscription;
  const trialDaysLeft = sub?.trial_end ? Math.max(0, Math.ceil((new Date(sub.trial_end).getTime() - Date.now()) / 86400000)) : 0;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 border-r border-border bg-card flex-col sticky top-0 h-screen">
        <div className="px-6 py-5 border-b border-border flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: "var(--brand-primary, hsl(var(--primary)))" }}>
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-display font-bold text-lg leading-none tracking-tight" data-testid="business-name-sidebar">{bname}</div>
            <div className="text-xs text-muted-foreground mt-1">{user?.is_platform_admin ? "Platform Admin" : user?.role === "owner" ? "Owner" : "Staff"}</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-foreground/70 hover:bg-muted hover:text-foreground"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
          {user?.is_platform_admin && (
            <NavLink
              to="/app/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition ${
                  isActive ? "bg-secondary/20 text-secondary-foreground" : "text-foreground/70 hover:bg-muted"
                }`
              }
            >
              <ShieldCheck className="h-4 w-4" />
              Platform Admin
            </NavLink>
          )}
        </nav>
        <div className="px-3 py-3 border-t border-border space-y-2">
          {sub?.status === "trialing" && (
            <div className="rounded-md bg-secondary/15 border border-secondary/30 p-3 text-xs" data-testid="trial-banner-desktop">
              <div className="font-semibold text-foreground">Free trial</div>
              <div className="text-muted-foreground">{trialDaysLeft} days left · <NavLink to="/app/settings?tab=subscription" className="underline text-secondary">Upgrade</NavLink></div>
            </div>
          )}
          <Button variant="ghost" className="w-full justify-start" onClick={handleLogout} data-testid="logout-button">
            <LogOut className="h-4 w-4 mr-2" /> Log out
          </Button>
          <div className="px-2 text-xs text-muted-foreground">{user?.email}</div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 h-14 px-4 flex items-center justify-between bg-card/90 backdrop-blur-xl border-b border-border">
        <button onClick={() => setSidebarOpen(true)} aria-label="Menu" data-testid="mobile-menu-open" className="p-2 -ml-2">
          <Menu className="h-5 w-5" />
        </button>
        <div className="font-display font-bold tracking-tight" data-testid="business-name-mobile">{bname}</div>
        <button onClick={handleLogout} aria-label="Logout" data-testid="mobile-logout" className="p-2 -mr-2">
          <LogOut className="h-5 w-5" />
        </button>
      </div>

      {/* Mobile sidebar drawer */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-foreground/40" onClick={() => setSidebarOpen(false)}></div>
          <div className="relative w-72 bg-card border-r border-border p-4 h-full overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-bold">{bname}</div>
              <button onClick={() => setSidebarOpen(false)} data-testid="mobile-menu-close"><X className="h-5 w-5" /></button>
            </div>
            <nav className="space-y-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`drawer-${n.testid}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${
                      isActive ? "bg-primary/10 text-primary" : "hover:bg-muted"
                    }`
                  }
                >
                  <n.icon className="h-4 w-4" />
                  {n.label}
                </NavLink>
              ))}
              {user?.is_platform_admin && (
                <NavLink to="/app/admin" onClick={() => setSidebarOpen(false)} className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium hover:bg-muted">
                  <ShieldCheck className="h-4 w-4" /> Platform Admin
                </NavLink>
              )}
            </nav>
          </div>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0 pt-14 md:pt-0 pb-20 md:pb-0">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-card border-t border-border h-16 flex items-stretch" data-testid="mobile-bottom-nav">
        {MOBILE_BOTTOM.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={n.testid}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center text-[11px] gap-0.5 ${
                isActive ? "text-primary" : "text-foreground/60"
              }`
            }
          >
            <n.icon className="h-5 w-5" />
            {n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
